/**
 * Re-extract that survives a slow server.
 *
 * Re-extract and Analyze IDF run the same operation (score Stage 1), and it
 * takes minutes on the current model — longer than a proxy waits. Analyze
 * IDF already recovers: when the request is lost it polls until the result
 * lands. Re-extract did not, so it showed "Re-extract failed" while the
 * server went on to finish (reviews 2-2 and 3-1; Analyze IDF then "worked").
 *
 * Analyze IDF finishes when scores appear. A re-extract starts from a
 * summary that already exists, so "done" has to mean "the summary changed".
 */

/** The request was lost on the way — a proxy cut-off or a dropped
 * connection — as opposed to the server answering with a reason. The
 * server is likely still working. Same test Analyze IDF applies. */
export function isLostRequest(e: any): boolean {
  const status = e?.response?.status;
  if (status === 504 || status === 502 || status === 408) return true;
  return !e?.response;
}

export const REEXTRACT_POLL_MS = 8_000;
export const REEXTRACT_MAX_WAIT_MS = 5 * 60 * 1000;

/** Poll until ``isChanged()`` is true or the budget is spent. Resolves true
 * when it changed. ``refresh`` is awaited between checks; the check runs
 * after the sleep so a refresh that just resolved has been rendered. */
export async function waitForChange({
  isChanged, refresh,
  pollMs = REEXTRACT_POLL_MS,
  maxMs = REEXTRACT_MAX_WAIT_MS,
  sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
}: {
  isChanged: () => boolean;
  refresh: () => Promise<unknown> | unknown;
  pollMs?: number;
  maxMs?: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<boolean> {
  let waited = 0;
  while (waited < maxMs) {
    await sleep(pollMs);
    waited += pollMs;
    if (isChanged()) return true;
    try {
      await refresh();
    } catch {
      // Soft-fail this tick; keep polling.
    }
  }
  return isChanged();
}
